import { createContext, useContext, useEffect, useState } from "react";
import { SinglePageLayoutContext } from "layout/SinglePageLayout";
import { useParams } from "react-router";
import Steps from "common/Steps/Steps";
import VerifyInformation from "./VerifyInformation";
import ProvideInformation from "./ProvideInformation";
import SubmitInformation from "./SubmitInformation";
import labels from "utils/LocalizedStrings";
import { useDispatch, useSelector } from "react-redux";
import cloneDeep from "lodash/cloneDeep";
import { useGetStaticDataQuery } from "service/APIService";
import Spinner from "common/Spinner/Spinner";
import {
  getStateName,
  getCountryName,
  getStateId,
  getRequiredIds,
  convertBlobToFile,
} from "utils/Utilities";
import { setAnnuityPayloadInStore } from "store/commonSlice";
import ErrorMessage from "common/ErrorMessage/ErrorMessage";

const TakeAnnuityOutOfPlan = () => {
  const singlePageLayoutContext = useContext(SinglePageLayoutContext);
  const { id } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [attachment, setAttachment] = useState("");
  const [accept, setAccept] = useState(".pdf");
  const [payload, setPayload] = useState({
    activityType: "Plan Separation",
    usCitizen: "true",
    nonResidentAlien: "false",
    consentAccepted: false,
    attachmentFormat: "PDF",
    beneficiaryDTOList: [],
    stateCode: "",
    countryCode: "",
  });

  const selectedTaxTypeInStore = useSelector(
    (state) => state.commonReducer.selectedTaxTypeInStore
  );
  const allocationsInStore = useSelector(
    (state) => state.commonReducer.allocationsInStore
  );
  const payloadInStore = useSelector(
    (state) => state.commonReducer.annuityPayloadInStore
  );
  const participantDetails = useSelector(
    (state) => state?.authReducer?.participantDetails
  );
  const balancesInStore = useSelector(
    (state) => state.commonReducer.balancesInStore
  );
  const { data: staticData, isFetching: loading1 } = useGetStaticDataQuery();

  const updatePayload = (value, key) => {
    if (key === "usCitizen" && value === "true") {
      payload["nonResidentAlien"] = "false";
    }
    if (key === "attachmentFormat") {
      setAccept(value === "PDF" ? ".pdf" : ".jpeg");
      sessionStorage.removeItem("image");
      updatePayload("", "attachment");
      setAttachment("");
    }
    payload[key] = value;
    setPayload({ ...payload });
  };

  const TakeAnnuityOutOfPlanContext = createContext({
    labels,
    payload,
    setPayload,
    selectedTaxTypeInStore,
    participantDetails,
    updatePayload,
    allocationsInStore,
    staticData,
    getStateName,
    getCountryName,
    setLoading,
    attachment,
    setAttachment,
    getStateId,
    getRequiredIds,
    setErrors,
    balancesInStore,
    accept,
    setAccept,
  });

  useEffect(() => {
    singlePageLayoutContext({
      title: labels?.takeAnnuityOutOfPlan?.title,
      buttonText: labels?.takeAnnuityOutOfPlan?.cancel,
      buttonPath: "/dashboard",
      description: labels?.takeAnnuityOutOfPlan?.description,
    });

    if (payloadInStore && payloadInStore?.activityType) {
      setPayload({ ...cloneDeep(payloadInStore) });
      setAccept(payloadInStore?.attachmentFormat === "PDF" ? ".pdf" : ".jpeg");
    } else {
      const clonedDetails = cloneDeep(participantDetails);
      setPayload({
        ...payload,
        ...clonedDetails,
        stateCode:
          clonedDetails?.usResident === "Y"
            ? clonedDetails?.pptStateCountryCode
            : "",
        countryCode:
          clonedDetails?.usResident === "Y"
            ? "US"
            : clonedDetails?.pptStateCountryCode,
      });
    }
  }, []);

  useEffect(() => {
    dispatch(setAnnuityPayloadInStore(cloneDeep(payload)));
  }, [payload]);

  useEffect(() => {
    if (attachment[0]) {
      const blobURL = URL.createObjectURL(attachment[0]);
      const obj = {
        fileType: attachment[0].type,
        fileName: attachment[0].name,
        blobURL: blobURL,
      };
      sessionStorage.setItem("image", JSON.stringify(obj));
    }
  }, [attachment]);

  useEffect(() => {
    const image = JSON.parse(sessionStorage.getItem("image"));
    if (image) {
      convertBlobToFile(image.blobURL, image.fileName, image.fileType)
        .then((res) => {
          if (res) setAttachment([res]);
        })
        .catch(() => {
          setAttachment("");
        });
    }
  }, []);

  if (id == 1) {
    return (
      <section className="single-page-container">
        {(loading1 || loading) && <Spinner />}
        {errors && errors.length
          ? errors.map((e, index) => <ErrorMessage key={index} error={e} />)
          : ""}
        <p className="second-header pt-2 pb-3">
          {labels?.takeAnnuityOutOfPlan?.verifyInformationHeader}
        </p>
        <Steps
          current="1"
          total="2"
          title={labels?.takeAnnuityOutOfPlan?.verifyInformationTitle}
          description={
            labels?.takeAnnuityOutOfPlan?.verifyInformationDescription
          }
          infoMsg={labels?.startIncome?.infoMsgInSubmitPage}
        />
        <VerifyInformation
          from="takeAnnuityOutOfPlan"
          context={TakeAnnuityOutOfPlanContext}
        />
      </section>
    );
  } else if (id == 2) {
    return (
      <section className="single-page-container">
        {(loading1 || loading) && <Spinner />}
        {errors && errors.length
          ? errors.map((e, index) => <ErrorMessage key={index} error={e} />)
          : ""}
        <Steps
          current="2"
          total="2"
          title={labels?.takeAnnuityOutOfPlan?.provideRequiredInformation}
          description={
            labels?.takeAnnuityOutOfPlan?.provideRequiredInformationDescription
          }
        />
        <ProvideInformation
          from="takeAnnuityOutOfPlan"
          context={TakeAnnuityOutOfPlanContext}
        />
      </section>
    );
  } else if (id == 3) {
    return (
      <section id="take-annuity-out-of-plan" className="single-page-container">
        {errors && errors.length
          ? errors.map((e, index) => <ErrorMessage key={index} error={e} />)
          : ""}
        {(loading1 || loading) && <Spinner />}
        <Steps
          title={
            labels?.takeAnnuityOutOfPlan?.pleaseVerifyRequiredInformationTitle
          }
          description={
            labels?.takeAnnuityOutOfPlan
              ?.pleaseVerifyRequiredInformationDescription
          }
        />
        <SubmitInformation
          from="takeAnnuityOutOfPlan"
          context={TakeAnnuityOutOfPlanContext}
          infoMsg={labels?.takeAnnuityOutOfPlan?.infoMsgInSubmitPage}
        />
      </section>
    );
  }
};

export default TakeAnnuityOutOfPlan;
